import { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { Upload, X } from 'lucide-react';
import './ImageUpload.css';

/** Must stay in sync with max upload size in storage.rules */
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

function ImageUpload({ onUploadComplete, currentImageUrl, folder = 'images' }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentImageUrl || '');

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setError(`Image must be ${MAX_IMAGE_BYTES / (1024 * 1024)}MB or smaller`);
      return;
    }

    setError('');
    setUploading(true);

    try {
      // Sanitize filename - remove special characters
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      
      // Create a unique filename
      const timestamp = Date.now();
      const filename = `${folder}/${timestamp}_${sanitizedName}`;
      const storageRef = ref(storage, filename);

      // Add metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'admin',
          originalName: file.name
        }
      };

      // Upload file with metadata
      await uploadBytes(storageRef, file, metadata);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      setPreview(downloadURL);
      onUploadComplete(downloadURL);
      setUploading(false);
    } catch (error) {
      console.error('Upload error:', error);
      if (error.code === 'storage/unauthorized') {
        setError('Permission denied. Make sure you are logged in as admin.');
      } else if (error.code === 'storage/canceled') {
        setError('Upload canceled.');
      } else if (error.code === 'storage/unknown') {
        setError('Upload failed. Check your internet connection.');
      } else {
        setError('Upload failed. Please try again.');
      }
      setUploading(false);
    }
  };

  const clearImage = () => {
    setPreview('');
    onUploadComplete('');
  };

  return (
    <div className="image-upload">
      {preview ? (
        <div className="image-preview-container">
          <img src={preview} alt="Preview" className="image-preview" />
          <button
            type="button"
            onClick={clearImage}
            className="image-remove-btn"
            title="Remove image"
          >
            <X size={20} />
          </button>
        </div>
      ) : (
        <div className="image-upload-area">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading}
            className="image-upload-input"
            id="image-upload-input"
          />
          <label htmlFor="image-upload-input" className="image-upload-label">
            <Upload size={32} />
            <span>{uploading ? 'Uploading...' : 'Click to upload image'}</span>
            <small>PNG, JPG, GIF, WebP up to 15MB</small>
          </label>
        </div>
      )}
      {error && <div className="upload-error">{error}</div>}
    </div>
  );
}

export default ImageUpload;
